/**
 * Soundscape — the ambience and the music, both generated.
 *
 * AMBIENCE is three noise beds and a bubble scheduler, each gated by where the shark actually
 * is: the surf only exists near the shore, the deep hum only exists over the drop-off. The
 * beds run continuously and are crossfaded rather than started and stopped, because a gain
 * ramp is inaudible and a source that starts mid-noise is a click.
 *
 * MUSIC is a slow generative piece in D dorian over a drone. Three voices — pad, sub, bell —
 * scheduled by a lookahead loop rather than from the render frame. That distinction matters
 * more than anything else here: notes are placed on the AudioContext's own clock with
 * setValueAtTime, so they land sample-accurately even while the renderer is dropping frames.
 * Triggering audio from requestAnimationFrame makes the music stutter whenever the graphics
 * do, and nothing gives away a cheap soundtrack faster.
 *
 * The scale is the reason it cannot sound wrong. Dorian over a static root has no leading
 * tone and no tritone against the drone, so any note of it lands as consonant — which is what
 * lets the pitches be chosen at random and still come out calm every time.
 */
import { Rng } from '../core/Rng.ts';
import type { AudioEngine } from './AudioEngine.ts';

/** D dorian, as semitone offsets from the root; two octaves for the bell to roam */
const SCALE = [0, 2, 3, 5, 7, 9, 10, 12, 14, 15, 17, 19, 21, 22, 24];
const ROOT_HZ = 146.83; // D3

/* The progression, as semitone offsets from the root. i - VII - i - IV in dorian: it never
   leaves the mode, so the drone stays true underneath and nothing ever has to resolve. */
const CHORDS: number[][] = [
  [0, 7, 12, 15],   // Dm
  [-2, 5, 10, 14],  // C
  [0, 7, 12, 15],   // Dm
  [5, 12, 17, 21],  // G
];

/** seconds per bar, and how far ahead the scheduler runs */
const BAR = 4.6;
const LOOKAHEAD = 0.6;
const TICK_MS = 120;

const hz = (semi: number): number => ROOT_HZ * Math.pow(2, semi / 12);

export class Soundscape {
  private readonly eng: AudioEngine;
  private readonly rng = new Rng(0x50a17d);

  // --- ambience beds
  private readonly hum: GainNode;
  private readonly wash: GainNode;
  private readonly surf: GainNode;
  private readonly bubbleBus: GainNode;

  // --- music
  private readonly padBus: GainNode;
  private readonly bellBus: GainNode;
  private readonly subBus: GainNode;
  private nextBar = 0;
  private bar = 0;
  private nextBubble = 0;
  private timer: number | null = null;
  private started = false;

  constructor(engine: AudioEngine) {
    this.eng = engine;
    const ctx = engine.ctx;

    /* ---- ambience -------------------------------------------------------------------
       Three bands off the same brown-noise buffer, each with its own filter. One source per
       bed rather than one shared: they need independent loop offsets or the three bands rise
       and fall together and the whole thing breathes as one, which sounds like a machine. */
    const bed = (type: BiquadFilterType, freq: number, q: number, gain: number, delay: number): GainNode => {
      const src = engine.noiseSource();
      const filt = ctx.createBiquadFilter();
      filt.type = type;
      filt.frequency.value = freq;
      filt.Q.value = q;
      const g = ctx.createGain();
      g.gain.value = gain;
      src.connect(filt).connect(g).connect(engine.ambience);
      src.start(ctx.currentTime + delay);
      return g;
    };
    // the body of the water: everything below a couple of hundred hertz
    this.hum = bed('lowpass', 220, 0.7, 0.30, 0);
    // mid wash — the broad hiss of moving water
    this.wash = bed('bandpass', 900, 0.5, 0.055, 0.31);
    // surf: bright, and only audible near the beach
    this.surf = bed('bandpass', 2600, 0.35, 0.0, 0.73);

    this.bubbleBus = ctx.createGain();
    this.bubbleBus.gain.value = 0.18;
    this.bubbleBus.connect(engine.ambience);

    /* ---- music ---------------------------------------------------------------------- */
    this.padBus = ctx.createGain();
    this.padBus.gain.value = 0.16;
    this.padBus.connect(engine.music);

    this.bellBus = ctx.createGain();
    this.bellBus.gain.value = 0.24;
    this.bellBus.connect(engine.music);

    this.subBus = ctx.createGain();
    this.subBus.gain.value = 0.30;
    this.subBus.connect(engine.music);
  }

  /** begin scheduling. Idempotent, and safe to call before the context is unlocked. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.nextBar = this.eng.ctx.currentTime + 0.4;
    this.nextBubble = this.nextBar;
    this.timer = window.setInterval(() => this.schedule(), TICK_MS);
  }

  /**
   * Per frame, from the render loop. Only crossfades — nothing here triggers a sound, so a
   * dropped frame costs a slightly late fade and never a missed note.
   *
   * @param depth       metres of water over the bed where the shark is
   * @param submerged   0 at the surface, 1 well under
   * @param shoreNear   0 offshore, 1 in the surf zone
   * @param speed       0..1 of cruise, for the flow noise
   */
  update(depth: number, submerged: number, shoreNear: number, speed: number): void {
    const ctx = this.eng.ctx;
    const t = ctx.currentTime;
    this.eng.setDepth(submerged);

    // Deep water is quiet and heavy; shallow water is busy. The hum comes up with depth.
    this.hum.gain.setTargetAtTime(0.22 + 0.20 * Math.min(1, depth / 30), t, 0.6);
    // Flow noise over the body rises with speed — the one cue that says you are moving when
    // the camera is locked to you and the seabed is the only other reference.
    this.wash.gain.setTargetAtTime(0.035 + 0.075 * speed, t, 0.35);
    /* Surf is audible near the beach AND near the surface: dive under it and it goes, which
       is exactly what happens in the water and costs nothing to reproduce. */
    this.surf.gain.setTargetAtTime(0.085 * shoreNear * (1 - submerged * 0.8), t, 0.7);
  }

  /**
   * The lookahead scheduler: place everything that falls inside the next window on the audio
   * clock, then return. This runs on a timer, deliberately decoupled from the frame.
   */
  private schedule(): void {
    if (!this.eng.running) return;
    const ctx = this.eng.ctx;
    const horizon = ctx.currentTime + LOOKAHEAD;

    while (this.nextBar < horizon) {
      this.playBar(this.nextBar, CHORDS[this.bar % CHORDS.length]);
      this.bar++;
      this.nextBar += BAR;
    }
    while (this.nextBubble < horizon) {
      this.bubble(this.nextBubble);
      this.nextBubble += this.rng.range(0.9, 5.5);
    }
  }

  private playBar(at: number, chord: number[]): void {
    const ctx = this.eng.ctx;

    // --- pad: the chord, held across the whole bar, detuned into a slow beat ------------
    for (const semi of chord) {
      for (const cents of [-6, 6]) {
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = hz(semi);
        o.detune.value = cents;
        const g = ctx.createGain();
        // Long attack and long release, overlapping the next bar: a pad with gaps between
        // chords is a series of events, and this has to be one continuous surface.
        g.gain.setValueAtTime(0.0001, at);
        g.gain.exponentialRampToValueAtTime(0.11, at + 1.6);
        g.gain.setValueAtTime(0.11, at + BAR * 0.55);
        g.gain.exponentialRampToValueAtTime(0.0001, at + BAR + 1.1);
        o.connect(g).connect(this.padBus);
        o.start(at);
        o.stop(at + BAR + 1.3);
      }
    }

    // --- sub: the root, an octave down, felt rather than heard --------------------------
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = hz(chord[0] - 12);
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.0001, at);
    sg.gain.exponentialRampToValueAtTime(0.22, at + 0.9);
    sg.gain.exponentialRampToValueAtTime(0.0001, at + BAR + 0.6);
    sub.connect(sg).connect(this.subBus);
    sub.start(at);
    sub.stop(at + BAR + 0.8);

    /* --- bell: sparse plucked notes ---------------------------------------------------
       Rests are as important as notes. Two to four in a bar out of eight possible slots means
       the phrase is mostly silence, and silence is what makes the individual notes land as
       deliberate rather than as a sequence. */
    const slots = 8;
    const wanted = 2 + this.rng.int(0, 2);
    for (let s = 0; s < slots; s++) {
      if (this.rng.next() > wanted / slots) continue;
      const semi = SCALE[this.rng.int(0, SCALE.length - 1)] + (this.rng.next() < 0.35 ? 12 : 0);
      this.bell(at + (s / slots) * BAR + this.rng.range(-0.02, 0.02), hz(semi), this.rng.range(0.55, 1));
    }
  }

  /** a struck tone: sine, instant attack, long exponential tail, wandering in the stereo field */
  private bell(at: number, freq: number, level: number): void {
    const ctx = this.eng.ctx;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = freq;
    // A touch of second harmonic gives it a body without making it a square wave.
    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.value = freq * 2.01;

    const g = ctx.createGain();
    const g2 = ctx.createGain();
    const pan = ctx.createStereoPanner();
    pan.pan.value = this.rng.range(-0.65, 0.65);

    const dur = 2.6 + this.rng.range(0, 1.4);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.30 * level, at + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    g2.gain.setValueAtTime(0.0001, at);
    g2.gain.exponentialRampToValueAtTime(0.07 * level, at + 0.008);
    g2.gain.exponentialRampToValueAtTime(0.0001, at + dur * 0.45);

    o.connect(g).connect(pan);
    o2.connect(g2).connect(pan);
    pan.connect(this.bellBus);
    o.start(at); o.stop(at + dur + 0.05);
    o2.start(at); o2.stop(at + dur * 0.5 + 0.05);
  }

  /** one bubble: a short sine sweeping UP, which is what a rising bubble actually does */
  private bubble(at: number): void {
    const ctx = this.eng.ctx;
    const o = ctx.createOscillator();
    o.type = 'sine';
    const f0 = this.rng.range(340, 900);
    o.frequency.setValueAtTime(f0, at);
    // The sweep is the whole tell. A bubble's resonant frequency rises as it rises and
    // shrinks under falling pressure; a flat blip just sounds like a beep.
    o.frequency.exponentialRampToValueAtTime(f0 * this.rng.range(1.8, 3.2), at + 0.09);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(this.rng.range(0.25, 0.7), at + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.11);
    const pan = ctx.createStereoPanner();
    pan.pan.value = this.rng.range(-0.8, 0.8);
    o.connect(g).connect(pan).connect(this.bubbleBus);
    o.start(at);
    o.stop(at + 0.14);
  }

  dispose(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }
}
